import Chart from 'bizcharts/lib/components/Chart'
import Axis from 'bizcharts/lib/components/Axis'
import Legend from 'bizcharts/lib/components/Legend'
import Tooltip from 'bizcharts/lib/components/Tooltip'
import Interval from 'bizcharts/lib/geometry/Interval'
import LineAdvance from 'bizcharts/lib/geometry/LineAdvance'

import { formatDigital } from '@/utils'
import { Spin } from '@arco-design/web-react'
import Mask from '../Mask'

interface DataItem {
  legend: string
  axis_x: string
  axis_y: number
  // 处理后的axis_y
  axis_y_f: string
}

interface DataItem2 {
  [key: string]: string | number
}

interface Props {
  data: DataItem[]
  height?: number
  loading?: boolean
  blur?: boolean
}

/** 双Y轴、折柱面积图，折线图表示总量，柱状图表示增量 */
function LineIntervalArea({ data = [], height = 300, loading = false, blur = false }: Props) {
  const formatData = data.reduce((acc, cur, idx, arr) => {
    const curItem = acc.find((item) => item?.axis_x === cur?.axis_x)

    if (curItem) {
      curItem[cur.legend] = cur?.axis_y
    } else {
      acc.push({
        axis_x: cur?.axis_x,
        [cur.legend]: cur?.axis_y
      })
    }
    return acc
  }, [] as DataItem2[])

  const dataAxisList = formatData.length > 0 ? Object.keys(formatData?.[0])?.filter((item) => item !== 'axis_x') : []
  const y = dataAxisList?.[0] || ''
  const z = dataAxisList?.[1] || ''

  const zMax = Math.max(...formatData.map((item) => item?.[z]))

  let chartIns = null
  const scale = {
    [y]: {
      alias: y,
      // tickCount: 5,
      min: 0,
      type: 'linear-strict'
    },
    [z]: {
      alias: z,
      // tickCount: 5,
      min: 0,
      // 将右轴拉长，使得增量柱状图只展示在下半图，避免过高超过总量折线
      max: zMax * 2,
      type: 'linear-strict'
    },
    axis_x: {
      mask: 'MM-DD'
    }
  }
  const colors = ['#655AEC', '#EB54AC']

  const label = {
    style: {
      fontSize: 12,
      fill: '#888BA6'
    },
    formatter(text: string) {
      return formatDigital(Number.isNaN(Number(text)) ? text : Number(text)).join('')
    }
  }
  return (
    <Spin loading={loading} style={{ width: '100%' }}>
      <Mask isBlur={blur} hasBtn={blur}>
        <Chart
          scale={scale}
          autoFit
          height={height}
          data={formatData}
          onGetG2Instance={(chart) => {
            chartIns = chart
          }}
          appendPadding={[0, 0, 40, 0]}
        >
          <Axis
            title
            name={y}
            label={label}
            grid={{
              line: {
                type: 'line',
                style: {
                  stroke: '#D7D9E8',
                  lineWidth: 1,
                  lineDash: [6, 4]
                }
              }
            }}
          />
          <Axis
            title
            name={z}
            label={label}
            grid={{
              line: {
                type: 'line',
                style: {
                  stroke: '#F0F1F8',
                  lineWidth: 1,
                  lineDash: [6, 4]
                }
              }
            }}
          />
          <Axis
            name="axis_x"
            label={label}
            line={{
              style: {
                stroke: '#F0F1F8',
                lineWidth: 1,
                lineDash: [6, 4]
              }
            }}
          />
          <Legend
            custom={true}
            allowAllCanceled={true}
            items={[
              {
                value: y,
                name: y,
                marker: {
                  symbol: 'circle',
                  style: { fill: colors[1], r: 5 }
                }
              },
              {
                value: z,
                name: z,
                marker: {
                  symbol: 'circle',
                  style: { fill: colors[0], r: 5 }
                }
              }
            ]}
            onChange={(ev) => {
              const item = ev.item
              const value = item.value
              const checked = !item.unchecked
              const geoms = chartIns.geometries

              for (let i = 0; i < geoms.length; i++) {
                const geom = geoms[i]

                if (geom.getYScale().field === value) {
                  if (checked) {
                    geom.show()
                  } else {
                    geom.hide()
                  }
                }
              }
            }}
          />
          <Tooltip
            shared
            showCrosshairs
            crosshairs={{
              line: {
                style: {
                  stroke: '#D7D9E8',
                  lineWidth: 1,
                  lineDash: [6, 4]
                }
              }
            }}
            showMarkers
          />
          <LineAdvance
            shape="smooth"
            area
            position={`axis_x*${y}`}
            color={colors[1]}
            tooltip={[
              `axis_x*${y}`,
              (axis_x, yValue) => {
                return {
                  name: y,
                  value: formatDigital(yValue).join(''),
                  title: axis_x
                }
              }
            ]}
          />
          <Interval
            position={`axis_x*${z}`}
            color={colors[0]}
            tooltip={[
              `axis_x*${z}`,
              (axis_x, zValue) => {
                return {
                  name: z,
                  value: formatDigital(zValue).join(''),
                  title: axis_x
                }
              }
            ]}
          />
        </Chart>
      </Mask>
    </Spin>
  )
}

export default LineIntervalArea
